import Badge from "../common/Badge";
import Button from "../common/Button";
import EmptyState from "../common/EmptyState";
import { getCampaignStatusMeta } from "../../utils/helpers";

const STATUS_FILTERS = [
  { value: "", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "scheduled", label: "Scheduled" },
  { value: "running", label: "Running" },
  { value: "paused", label: "Paused" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

function formatCampaignName(name) {
  const text = String(name || "Untitled campaign").trim();

  if (!text) {
    return "Untitled campaign";
  }

  if (text === text.toUpperCase() && /[A-Z]/.test(text)) {
    return text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return text;
}

// Truncate at a word boundary so descriptions don't cut mid-word.
function truncateAtWord(text, max) {
  const value = String(text || "").trim();

  if (value.length <= max) {
    return value;
  }

  const slice = value.slice(0, max);
  const lastSpace = slice.lastIndexOf(" ");

  return (lastSpace > 0 ? slice.slice(0, lastSpace) : slice).trimEnd();
}

export default function CampaignList({
  campaigns,
  loading,
  statusFilter = "",
  onStatusFilterChange,
  onCreate,
  onView,
  onDelete,
  onAction,
  actionBusy = false,
}) {
  const visibleCampaigns = statusFilter
    ? campaigns.filter((campaign) => String(campaign.status || "") === statusFilter)
    : campaigns;

  return (
    <div>
      <div className="section-row">
        <div className="section-title section-title-no-margin">Campaigns</div>
        <div className="section-tools">
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(event) => onStatusFilterChange?.(event.target.value)}
            aria-label="Filter campaigns by status"
          >
            {STATUS_FILTERS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Button variant="gold" onClick={onCreate}>
            + New Campaign
          </Button>
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Answered</th>
              <th>Interested</th>
              <th>Booked</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7">
                  <div className="empty-state">Loading campaigns...</div>
                </td>
              </tr>
            ) : !visibleCampaigns.length ? (
              <tr>
                <td colSpan="7">
                  <EmptyState
                    title={statusFilter ? "No campaigns match this status" : "No campaigns available"}
                    subtitle={
                      statusFilter
                        ? "Change the status filter to see more campaigns."
                        : "Create one to get started."
                    }
                  />
                </td>
              </tr>
            ) : (
              visibleCampaigns.map((campaign) => {
                const status = getCampaignStatusMeta(campaign.status);
                const percentage = campaign.total_targets
                  ? Math.round((campaign.calls_made / campaign.total_targets) * 100)
                  : 0;
                const offerText = campaign.offer_message || campaign.description || "";

                return (
                  <tr key={campaign.id}>
                    <td>
                      <strong>{formatCampaignName(campaign.name)}</strong>
                      <br />
                      <span className="micro-copy" title={offerText}>
                        {offerText ? truncateAtWord(offerText, 80) : "No description"}
                        {offerText.length > 80 ? "..." : ""}
                      </span>
                      {campaign.scheduled_at && campaign.status === "scheduled" ? (
                        <>
                          <br />
                          <span className="micro-copy muted-text">
                            Scheduled: {new Date(campaign.scheduled_at).toLocaleString()}
                          </span>
                        </>
                      ) : null}
                    </td>
                    <td>
                      <Badge variant={status.variant}>{status.label}</Badge>
                    </td>
                    <td>
                      <div className="campaign-progress-mini">
                        <div className="campaign-progress-mini-row">
                          <span>{`${campaign.calls_made || 0}/${campaign.total_targets || 0}`}</span>
                          <span>{percentage}%</span>
                        </div>
                        <div className="campaign-progress-track">
                          <div
                            className="campaign-progress-fill"
                            style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>{campaign.calls_answered || 0}</td>
                    <td>{campaign.calls_interested || 0}</td>
                    <td>{campaign.calls_booked || 0}</td>
                    <td className="campaign-actions-cell">
                      <Button
                        className="compact-btn"
                        onClick={() => onView(campaign.id)}
                      >
                        View
                      </Button>
                      {campaign.status === "running" && onAction ? (
                        <Button
                          className="compact-btn"
                          disabled={actionBusy}
                          onClick={() => onAction(campaign.id, "pause")}
                        >
                          Pause
                        </Button>
                      ) : null}
                      {campaign.status === "paused" && onAction ? (
                        <Button
                          className="compact-btn"
                          disabled={actionBusy}
                          onClick={() => onAction(campaign.id, "resume")}
                        >
                          Resume
                        </Button>
                      ) : null}
                      {["running", "paused"].includes(campaign.status) && onAction ? (
                        <Button
                          variant="danger"
                          className="compact-btn"
                          disabled={actionBusy}
                          onClick={() => onAction(campaign.id, "cancel")}
                        >
                          Cancel
                        </Button>
                      ) : null}
                      {["draft", "scheduled", "completed", "cancelled"].includes(campaign.status) ? (
                        <Button
                          variant="danger"
                          className="compact-btn"
                          onClick={() => onDelete(campaign.id)}
                        >
                          Delete
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
